import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

// Library Management
export interface LibraryStatus {
  id: string
  display_name: string
  description: string
  enabled: boolean // Legacy: kept for backward compatibility
  search_enabled: boolean // New: whether users can search this library
  indexing_enabled: boolean // New: whether indexer processes this library
  document_count?: number
  blocked_modules: string[]
  source?: string // "migrated_from_config" | "user_created"
  access_configured?: boolean
  access_overridden?: boolean
}

export async function updateLibraryStatus(
  libraryId: string,
  enabled: boolean
): Promise<{
  success: boolean
  library_id: string
  enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/libraries/${libraryId}/status`, { enabled })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || `Failed to update library ${libraryId} status`)
  }

  return data
}

export async function getLibraryStatuses(): Promise<Record<string, { enabled: boolean }>> {
  const response = await api.get("/admin/libraries/status")
  return response.data || response
}

export async function updateLibrariesStatusBatch(updates: Record<string, boolean>): Promise<{
  success: boolean
  updated_libraries: string[]
  message: string
}> {
  const response = await api.put("/admin/libraries/status/batch", { updates })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update library statuses")
  }

  return data
}

// New: Update search_enabled status (whether users can search this library)
export async function updateLibrarySearchEnabled(
  libraryId: string,
  enabled: boolean
): Promise<{
  success: boolean
  library_id: string
  search_enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/libraries/${libraryId}/search-enabled`, {
    enabled,
  })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update library ${libraryId} search status`)
  }

  return data
}

// New: Update indexing_enabled status (whether indexer processes this library)
export async function updateLibraryIndexingEnabled(
  libraryId: string,
  enabled: boolean
): Promise<{
  success: boolean
  library_id: string
  indexing_enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/libraries/${libraryId}/indexing-enabled`, {
    enabled,
  })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update library ${libraryId} indexing status`)
  }

  return data
}

export async function getLibraryAccessPolicy(libraryId: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/libraries/${libraryId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function getLibraryBaseAccessPolicy(): Promise<AccessPolicy> {
  const response = await api.get("/admin/libraries/base/access")
  const data = response.data || response
  return data.access || {}
}

export async function updateLibraryAccessPolicy(
  libraryId: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/libraries/${libraryId}/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function updateLibraryBaseAccessPolicy(
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put("/admin/libraries/base/access", policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearLibraryAccessPolicy(libraryId: string): Promise<AccessPolicy> {
  const response = await api.put(`/admin/libraries/${libraryId}/access`, {})
  const data = response.data || response
  return data.access || {}
}

// =============================================================================
// Library Management (Cosmos DB)
// =============================================================================

// Auth configuration for sources requiring authentication
// Note: client_secret is stored as @kv: reference, never the actual secret
// When returned from API, it's either masked ("***configured***") or the reference name
export type AuthConfig = {
  client_id?: string
  tenant_id?: string
  client_secret?: string // Key Vault secret name or masked value
}

// Source config types for different library sources
export type SharePointSourceConfig = {
  hostname?: string
  siteName?: string
  listName?: string
  listId?: string
  siteId?: string
  sharepoint_url?: string // URL used during creation, parsed into above fields
  /** Scope indexing to a specific folder inside the library (e.g. "Specs/Drafts"). Empty = whole library. */
  folderPath?: string
  /** Read each document's real SharePoint permissions via Graph and map them onto its ACL fields. */
  replicate_permissions?: boolean
  extra_data_definition?: unknown[]
  language?: string // Language code for document processing
  exclude_folders?: string[] // Folder names to skip during indexing
  content_type?: string // Content type filter
  page?: Record<string, unknown> // Page-specific configuration
  list_item?: Record<string, unknown> // List item-specific configuration
  auth?: AuthConfig
}

export type BlobSourceConfig = {
  input_container?: string
}

export type DynamicsSourceConfig = {
  environment_url?: string
  entity_name?: string
  auth?: AuthConfig
}

/** External sources are API-driven, minimal config */
export type ExternalSourceConfig = Record<string, never>

export type SourceConfig =
  | SharePointSourceConfig
  | BlobSourceConfig
  | DynamicsSourceConfig
  | ExternalSourceConfig

export type LibrarySourceType =
  | "sharepoint"
  | "spo"
  | "spo-avtal"
  | "spo-list"
  | "spo-pages"
  | "blob"
  | "dynamics"
  | "external"
  | "http"

export type LibraryConfig = {
  id: string
  display_name: string
  description?: string
  source_type: LibrarySourceType | string
  source_config: SourceConfig | Record<string, unknown>
  embedding_model_id: string
  index?: string
  index_name?: string
  output_container?: string
  output_table?: string
  enabled?: boolean // Legacy
  search_enabled?: boolean // Whether users can search this library
  indexing_enabled?: boolean // Whether indexer processes this library
  exclude_patterns?: string[]
  blocked_modules?: string[] // Routes that are blocked from accessing this library
  chunk_size?: number | null // Document chunk size for text splitters (null = use default)
  /** Show the original file in previews instead of the processed rendition. */
  use_unmodified_previews?: boolean
  /** Reuse stored auth from another library (server-side merge); optional on create and update */
  copy_auth_from_library_id?: string
  /** FILTH retrieval backend (issues #2305/#2307). */
  search_backend?: "azure-search" | "filth"
  filth_backend_id?: string
  filth_scope?: string
  filth_source_uids?: string[]
  company_wide?: boolean
}

export type Library = LibraryConfig & {
  created_at?: string
  updated_at?: string
  source?: "migrated_from_config" | "user_created"
}

export type LibrariesResponse = {
  libraries: Library[]
}

export async function getLibraries(): Promise<LibrariesResponse> {
  const response = await api.get("/admin/libraries/dynamic")
  return response.data || response
}

export async function createLibrary(config: LibraryConfig): Promise<{
  success: boolean
  library_id: string
  library?: Library
  message: string
}> {
  const response = await api.post("/admin/libraries", config)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to create library")
  }

  return data
}

export async function updateLibraryConfig(
  libraryId: string,
  config: Partial<LibraryConfig>
): Promise<{
  success: boolean
  library_id: string
  library?: Library
  message: string
}> {
  const response = await api.put(`/admin/libraries/${libraryId}/config`, config)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update library ${libraryId}`)
  }

  return data
}

export type DeleteLibraryOptions = {
  deleteIndex?: boolean
  deleteTable?: boolean
  deleteContainer?: boolean
}

export async function deleteLibrary(
  libraryId: string,
  options?: DeleteLibraryOptions
): Promise<{
  success: boolean
  library_id: string
  message: string
  deleted_resources?: string[]
}> {
  // Build query params for delete options
  const params = new URLSearchParams()
  if (options?.deleteIndex) params.append("delete_index", "true")
  if (options?.deleteTable) params.append("delete_table", "true")
  if (options?.deleteContainer) params.append("delete_container", "true")

  const queryString = params.toString()
  const url = `/admin/libraries/${libraryId}${queryString ? `?${queryString}` : ""}`

  const response = await api.delete(url)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to delete library ${libraryId}`)
  }

  return data
}

// =============================================================================
// Tree-walk retrieval: coverage + backfill orchestration
// =============================================================================

export type LibraryTreeCoverageResponse = {
  total: number | null
  with_tree: number | null
  without_tree: number
  coverage_pct: number | null
  missing_files: string[]
}

export type LibraryTreeBackfillActiveResponse = {
  job_id: string | null
}

export type AllActiveBackfillsResponse = {
  active: Record<string, string>
}

export type AllTreeCoverageResponse = {
  coverage: Record<
    string,
    {
      total: number
      with_tree: number
      without_tree: number
      coverage_pct: number
    }
  >
}

export type LibraryTreeBackfillRestartResponse = {
  job_id: string
  prior_job_id: string
}

export type LibraryTreeBackfillStartRequest = {
  batch_size?: number
  overwrite?: boolean
}

export type LibraryTreeBackfillStartResponse = {
  job_id: string
}

export type LibraryTreeBackfillStatusResponse =
  | {
      job: Record<string, unknown>
      isStale: boolean
    }
  | {
      orphanLockCleared: true
      error?: string
      job_id?: string
      library_id?: string
    }
  | {
      jobNotFound: true
      error?: string
      job_id?: string
      library_id?: string
    }

export type LibraryTreeBackfillCancelResponse = {
  success: boolean
  orphanLockCleared?: boolean
}

export async function getLibraryTreeCoverage(
  libraryId: string
): Promise<LibraryTreeCoverageResponse> {
  const response = await api.get(`/admin/libraries/${encodeURIComponent(libraryId)}/tree/coverage`)
  return response.data || response
}

export async function getLibraryTreeBackfillActive(
  libraryId: string
): Promise<LibraryTreeBackfillActiveResponse> {
  const response = await api.get(
    `/admin/libraries/${encodeURIComponent(libraryId)}/tree/backfill/active`
  )
  return response.data || response
}

export async function startLibraryTreeBackfill(
  libraryId: string,
  body: LibraryTreeBackfillStartRequest
): Promise<LibraryTreeBackfillStartResponse> {
  const response = await api.post(
    `/admin/libraries/${encodeURIComponent(libraryId)}/tree/backfill`,
    body
  )
  return response.data || response
}

export async function getLibraryTreeBackfillStatus(
  libraryId: string,
  jobId: string
): Promise<LibraryTreeBackfillStatusResponse> {
  try {
    const response = await api.get(
      `/admin/libraries/${encodeURIComponent(libraryId)}/tree/backfill/${encodeURIComponent(jobId)}`
    )
    return response.data || response
  } catch (e: unknown) {
    const err = e as {
      response?: {
        status?: number
        data?: {
          orphanLockCleared?: boolean
          jobNotFound?: boolean
          error?: string
          job_id?: string
          library_id?: string
        }
      }
    }
    const status = err.response?.status
    const d = err.response?.data
    if (status === 410 && d?.orphanLockCleared) {
      return {
        orphanLockCleared: true,
        error: typeof d.error === "string" ? d.error : undefined,
        job_id: typeof d.job_id === "string" ? d.job_id : undefined,
        library_id: typeof d.library_id === "string" ? d.library_id : undefined,
      }
    }
    if (status === 410 && d?.jobNotFound) {
      return {
        jobNotFound: true,
        error: typeof d.error === "string" ? d.error : undefined,
        job_id: typeof d.job_id === "string" ? d.job_id : undefined,
        library_id: typeof d.library_id === "string" ? d.library_id : undefined,
      }
    }
    // Legacy 404 or misconfigured proxies — treat as gone so the UI stops polling.
    if (status === 404) {
      return {
        jobNotFound: true,
        error: typeof d?.error === "string" ? d.error : "Job not found",
        job_id: jobId,
        library_id: libraryId,
      }
    }
    throw e
  }
}

export async function cancelLibraryTreeBackfill(
  libraryId: string,
  jobId: string
): Promise<LibraryTreeBackfillCancelResponse> {
  const response = await api.post(
    `/admin/libraries/${encodeURIComponent(libraryId)}/tree/backfill/${encodeURIComponent(jobId)}/cancel`
  )
  return response.data || response
}

export async function getAllActiveBackfills(): Promise<AllActiveBackfillsResponse> {
  const response = await api.get("/admin/libraries/tree/backfill/active/all")
  return response.data || response
}

export async function getAllTreeCoverage(): Promise<AllTreeCoverageResponse> {
  const response = await api.get("/admin/libraries/tree/coverage/all")
  return response.data || response
}

export async function restartLibraryTreeBackfill(
  libraryId: string,
  jobId: string,
  body: LibraryTreeBackfillStartRequest = {}
): Promise<LibraryTreeBackfillRestartResponse> {
  const response = await api.post(
    `/admin/libraries/${encodeURIComponent(libraryId)}/tree/backfill/${encodeURIComponent(jobId)}/restart`,
    body
  )
  return response.data || response
}
